import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { commandRegistry } from '@/commands/registry';
import { setPendingColor, setPendingTextColor, setPendingFontSize } from '@/commands/styleCommands';
import type { CommandContext } from '@/commands/types';
import type { NodeBounds } from '@/layout/types';
import { useTheme } from '@/theme/themeContext';
import { getStyleForLevel } from '@/theme/defaultTheme';

const COLOR_PALETTE = [
  '#6366f1', '#3b82f6', '#0891b2', '#059669',
  '#16a34a', '#ca8a04', '#ea580c', '#dc2626',
  '#db2777', '#9333ea', '#334155', '#64748b',
];

function getRecentColors(): string[] {
  try {
    const stored = localStorage.getItem('ideatwig-recent-colors');
    return stored ? JSON.parse(stored) as string[] : [];
  } catch { return []; }
}

function addRecentColor(color: string): void {
  const recent = getRecentColors().filter(c => c !== color);
  recent.unshift(color);
  localStorage.setItem('ideatwig-recent-colors', JSON.stringify(recent.slice(0, 6)));
}

function getContext(): CommandContext {
  return {
    doc: useDocumentStore.getState().mapDoc,
    editorState: useEditorStore.getState(),
  };
}

interface NodeToolbarProps {
  bounds: NodeBounds;
  panX: number;
  panY: number;
  zoom: number;
}

export function NodeToolbar({ bounds, panX, panY, zoom }: NodeToolbarProps) {
  const { t } = useTranslation();
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const contextMenuOpen = useEditorStore((s) => s.contextMenuOpen);
  const editingNodeId = useEditorStore((s) => s.editingNodeId);
  const multiSelectMode = useEditorStore((s) => s.multiSelectMode);
  const toggleMultiSelect = useEditorStore((s) => s.toggleMultiSelect);
  const nodes = useDocumentStore((s) => s.nodes);

  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [recentColors, setRecentColors] = useState<string[]>(getRecentColors);

  const selectedId = selectedNodeIds[0];
  const node = selectedId ? nodes.get(selectedId) : undefined;

  // Reset drag offset and pickers when selected node changes
  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
    setShowBgPicker(false);
    setShowTextColorPicker(false);
  }, [selectedId]);

  // Drag handlers for the grab handle
  const onDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const clientX = 'touches' in e ? e.touches[0]!.clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0]!.clientY : e.clientY;
    dragStart.current = { x: clientX, y: clientY, offsetX: dragOffset.x, offsetY: dragOffset.y };
    setIsDragging(true);
  }, [dragOffset]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0]!.clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0]!.clientY : e.clientY;
      setDragOffset({
        x: dragStart.current.offsetX + (clientX - dragStart.current.x),
        y: dragStart.current.offsetY + (clientY - dragStart.current.y),
      });
    };
    const onEnd = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isDragging]);

  // Horizontal scroll with mouse wheel
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const theme = useTheme();

  if (!node || contextMenuOpen || editingNodeId) return null;

  const levelStyle = getStyleForLevel(theme, bounds.depth);
  const currentFontSize = node.style?.fontSize ?? levelStyle.fontSize;
  const effectiveFontWeight = node.style?.fontWeight ?? levelStyle.fontWeight;
  const isBold = effectiveFontWeight === 'bold';
  const isItalic = node.style?.fontStyle === 'italic';
  const isUnderline = node.style?.textDecoration === 'underline';
  const isStrikethrough = node.style?.textDecoration === 'line-through';
  const currentBg = node.style?.backgroundColor ?? levelStyle.backgroundColor;
  const currentTextColor = node.style?.textColor ?? levelStyle.textColor;

  // Position: above the node, centered, in screen coordinates
  const screenX = bounds.centerX * zoom + panX + dragOffset.x;
  const screenY = bounds.y * zoom + panY - 12 + dragOffset.y; // 12px gap above node

  const handleBgColor = (color: string) => {
    setPendingColor(color);
    commandRegistry.execute('style.setColor', getContext());
    addRecentColor(color);
    setRecentColors(getRecentColors());
  };

  const handleTextColor = (color: string) => {
    setPendingTextColor(color);
    commandRegistry.execute('style.setTextColor', getContext());
    addRecentColor(color);
    setRecentColors(getRecentColors());
  };

  const handleFontSizeChange = (value: number) => {
    setPendingFontSize(value);
    commandRegistry.execute('style.setFontSize', getContext());
  };

  const iconBtnStyle = (active: boolean): React.CSSProperties => ({
    background: active ? 'var(--bg-surface-raised)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
    border: active ? '1px solid var(--border-strong)' : '1px solid transparent',
    borderRadius: 4,
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  });

  const separator = <div style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '0 2px', flexShrink: 0 }} />;

  return (
    <div
      style={{
        position: 'fixed',
        left: screenX,
        bottom: 'auto',
        top: screenY,
        transform: 'translate(-50%, -100%)',
        zIndex: 25,
        pointerEvents: 'auto',
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Background color picker dropdown */}
      {showBgPicker && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: 8,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 8,
          padding: 8,
          boxShadow: 'var(--shadow-md)',
          zIndex: 26,
        }}>
          <input
            type="color"
            value={currentBg || '#6366f1'}
            onChange={(e) => { handleBgColor(e.target.value); }}
            style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer', borderRadius: 4, marginBottom: 6 }}
            title={t('panels.backgroundColor')}
          />
          {recentColors.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>Recent</div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                {recentColors.map(color => (
                  <button key={color} onClick={() => { handleBgColor(color); setShowBgPicker(false); }}
                    style={{ width: 24, height: 24, borderRadius: 4, background: color, border: '2px solid transparent', cursor: 'pointer', padding: 0 }}
                  />
                ))}
              </div>
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                onClick={() => { handleBgColor(color); setShowBgPicker(false); }}
                style={{
                  width: 24, height: 24, borderRadius: 4, background: color,
                  border: currentBg === color ? '2px solid var(--text-primary)' : '2px solid transparent',
                  cursor: 'pointer', padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Text color picker dropdown */}
      {showTextColorPicker && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: 8,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 8,
          padding: 8,
          boxShadow: 'var(--shadow-md)',
          zIndex: 26,
        }}>
          <input
            type="color"
            value={currentTextColor || '#e2e8f0'}
            onChange={(e) => { handleTextColor(e.target.value); }}
            style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer', borderRadius: 4, marginBottom: 6 }}
            title={t('panels.textColor')}
          />
          {recentColors.length > 0 && (
            <>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>Recent</div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                {recentColors.map(color => (
                  <button key={color} onClick={() => { handleTextColor(color); setShowTextColorPicker(false); }}
                    style={{ width: 24, height: 24, borderRadius: 4, background: color, border: '2px solid transparent', cursor: 'pointer', padding: 0 }}
                  />
                ))}
              </div>
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
            {['#ffffff', '#e2e8f0', '#94a3b8', '#0f172a', '#ef4444', '#f59e0b',
              '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b', '#000000'].map((color) => (
              <button
                key={color}
                onClick={() => { handleTextColor(color); setShowTextColorPicker(false); }}
                style={{
                  width: 24, height: 24, borderRadius: 4, background: color,
                  border: currentTextColor === color ? '2px solid var(--color-primary)' : '1px solid var(--border-default)',
                  cursor: 'pointer', padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main toolbar bar */}
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 'min(90vw, 420px)',
      }}>
        {/* Grab handle */}
        <div
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          style={{
            width: 32,
            height: 6,
            borderRadius: 3,
            background: 'var(--border-default)',
            margin: '4px 0 2px',
            cursor: isDragging ? 'grabbing' : 'grab',
            flexShrink: 0,
          }}
        />

        {/* Scrollable icon row */}
        <div
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '4px 6px',
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100%',
            scrollbarWidth: 'none',
          }}
        >
          {/* Bold */}
          <button onClick={() => commandRegistry.execute('style.bold', getContext())} style={iconBtnStyle(isBold)} title={`${t('panels.fontWeight')} (Ctrl+B)`}>
            <strong style={{ fontSize: 13 }}>B</strong>
          </button>

          {/* Italic */}
          <button onClick={() => commandRegistry.execute('style.toggleItalic', getContext())} style={iconBtnStyle(isItalic)} title={`${t('panels.italic')} (Ctrl+I)`}>
            <em style={{ fontSize: 13, fontFamily: 'serif' }}>I</em>
          </button>

          {/* Underline */}
          <button onClick={() => commandRegistry.execute('style.toggleUnderline', getContext())} style={iconBtnStyle(isUnderline)} title={`${t('panels.underline')} (Ctrl+U)`}>
            <span style={{ fontSize: 13, textDecoration: 'underline' }}>U</span>
          </button>

          {/* Strikethrough */}
          <button onClick={() => commandRegistry.execute('style.toggleStrikethrough', getContext())} style={iconBtnStyle(isStrikethrough)} title={t('panels.strikethrough')}>
            <span style={{ fontSize: 13, textDecoration: 'line-through' }}>S</span>
          </button>

          {separator}

          {/* Font size: numeric input */}
          <input
            type="number"
            min={8}
            max={72}
            value={currentFontSize}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) handleFontSizeChange(v);
            }}
            style={{
              width: 42,
              height: 28,
              background: 'var(--bg-app)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              borderRadius: 4,
              textAlign: 'center',
              fontSize: 12,
              fontFamily: 'var(--font-family)',
              padding: '0 2px',
              flexShrink: 0,
            }}
            title={t('panels.fontSize')}
            onClick={(e) => e.stopPropagation()}
          />
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)', flexShrink: 0 }}>px</span>

          {separator}

          {/* Background color button */}
          <button
            onClick={() => { if (!showBgPicker) setRecentColors(getRecentColors()); setShowBgPicker(!showBgPicker); setShowTextColorPicker(false); }}
            style={{
              ...iconBtnStyle(showBgPicker),
              position: 'relative',
            }}
            title={t('panels.backgroundColor')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
            </svg>
            <div style={{
              position: 'absolute', bottom: 1, left: 4, right: 4, height: 3,
              background: currentBg || 'var(--text-secondary)', borderRadius: 1,
            }} />
          </button>

          {/* Text color button */}
          <button
            onClick={() => { if (!showTextColorPicker) setRecentColors(getRecentColors()); setShowTextColorPicker(!showTextColorPicker); setShowBgPicker(false); }}
            style={{
              ...iconBtnStyle(showTextColorPicker),
              position: 'relative',
            }}
            title={t('panels.textColor')}
          >
            <span style={{ fontSize: 14, fontWeight: 700 }}>A</span>
            <div style={{
              position: 'absolute', bottom: 1, left: 4, right: 4, height: 3,
              background: currentTextColor || 'var(--text-primary)', borderRadius: 1,
            }} />
          </button>

          {separator}

          {/* Duplicate */}
          <button onClick={() => commandRegistry.execute('clipboard.duplicate', getContext())} style={iconBtnStyle(false)} title="Duplicate (Ctrl+D)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>

          {/* Copy */}
          <button onClick={() => commandRegistry.execute('clipboard.copy', getContext())} style={iconBtnStyle(false)} title="Copy (Ctrl+C)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>

          {/* Paste */}
          <button onClick={() => commandRegistry.execute('clipboard.paste', getContext())} style={iconBtnStyle(false)} title="Paste (Ctrl+V)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <rect x="8" y="2" width="8" height="4" rx="1" />
            </svg>
          </button>

          {separator}

          {/* Multi-select toggle */}
          <button
            onClick={toggleMultiSelect}
            style={{
              ...iconBtnStyle(multiSelectMode),
              position: 'relative',
            }}
            title={t('toolbar.multiSelect')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
              <path d="M13 13l6 6" />
            </svg>
          </button>

          {/* Selection count badge */}
          {selectedNodeIds.length > 1 && (
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--text-on-primary)',
              background: 'var(--color-primary)',
              borderRadius: 8,
              padding: '1px 6px',
              flexShrink: 0,
              lineHeight: '16px',
            }}>
              {selectedNodeIds.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
